import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import { getWeekSchedule, convertTimeTo12HourFormat } from '../../../utils/helpers/venues/venuesHelpers';
import { selectCurrentVenue } from '../../../redux/slices/currentUserSlice';
import { selectBusinessHoursAndStatus } from '../../../redux/venues/venuesSlice';
import { fetchBusinessHours } from '../../../redux/venues/venuesThunks';
import {
  OperatingHoursContainer,
  OperatingHoursGrid,
  HeaderRow,
  HeaderCell,
  HeaderText,
  TimeRow,
  DayCell,
  TimeCell,
  TimeSelect,
  GridContainer,
} from './OperatingHours.styles';

const OperatingHours = ({ startOfWeekIndex = 1 }) => {
  const dispatch = useDispatch();
  const currentVenue = useSelector(selectCurrentVenue);
  const { businessHours, status } = useSelector(selectBusinessHoursAndStatus);

  useEffect(() => {
    if (currentVenue?.id) {
      dispatch(fetchBusinessHours(currentVenue.id));
    }
  }, [dispatch, currentVenue?.id]);

  const schedule = getWeekSchedule(startOfWeekIndex, businessHours.length > 0 ? businessHours : null);
  const isLoading = status === 'loading';

  return (
    <OperatingHoursContainer>
      <>
        <Typography variant="h6">Operating hours</Typography>
        <Typography variant="subtitle2">
          <em>{isLoading ? 'Loading hours from your POS system...' : 'All fields are populated from your POS system.'}</em>
        </Typography>
      </>

      <OperatingHoursGrid>
        <GridContainer>
          <HeaderRow>
            <HeaderCell className="first-column">
              <HeaderText></HeaderText>
            </HeaderCell>
            <HeaderCell className="span-2">
              <HeaderText>Hours open to the public</HeaderText>
            </HeaderCell>
            <HeaderCell className="last-column">
              <HeaderText>Work day hours</HeaderText>
            </HeaderCell>
          </HeaderRow>

          {schedule.map((daySchedule, index) => (
            <TimeRow key={daySchedule.dayOfWeek}>
              <DayCell className={index === schedule.length - 1 ? 'last-row' : ''}>
                <Typography color="white">{daySchedule.dayOfWeek}</Typography>
              </DayCell>

              <TimeCell
                className={[
                  index === schedule.length - 1 ? 'last-row' : '',
                  daySchedule.available !== 1 ? 'closed' : '',
                  !daySchedule.publicSplitFrom && !daySchedule.publicSplitTo ? 'span-2' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <TimeSelect>
                  <Typography>
                    {daySchedule.available === 1 ? convertTimeTo12HourFormat(daySchedule.publicFrom) : ''}
                  </Typography>
                </TimeSelect>
                {daySchedule.available === 1 && <Typography color="white">-</Typography>}
                <TimeSelect>
                  <Typography>
                    {daySchedule.available === 1 ? convertTimeTo12HourFormat(daySchedule.publicTo) : ''}
                  </Typography>
                </TimeSelect>
              </TimeCell>

              {(daySchedule.publicSplitFrom || daySchedule.publicSplitTo) && (
                <TimeCell
                  className={[
                    index === schedule.length - 1 ? 'last-row' : '',
                    daySchedule.available !== 1 ? 'closed' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <TimeSelect>
                    <Typography>
                      {daySchedule.available === 1 && daySchedule.publicSplitFrom
                        ? convertTimeTo12HourFormat(daySchedule.publicSplitFrom)
                        : ''}
                    </Typography>
                  </TimeSelect>
                  {daySchedule.available === 1 && daySchedule.publicSplitFrom && (
                    <Typography color="white">-</Typography>
                  )}
                  <TimeSelect>
                    <Typography>
                      {daySchedule.available === 1 && daySchedule.publicSplitTo
                        ? convertTimeTo12HourFormat(daySchedule.publicSplitTo)
                        : ''}
                    </Typography>
                  </TimeSelect>
                </TimeCell>
              )}

              <TimeCell
                className={[
                  'last-column',
                  index === schedule.length - 1 ? 'last-row' : '',
                  daySchedule.available !== 1 ? 'closed' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <TimeSelect>
                  <Typography>
                    {daySchedule.available === 1 ? convertTimeTo12HourFormat(daySchedule.workdayFrom) : ''}
                  </Typography>
                </TimeSelect>
                {daySchedule.available === 1 && <Typography color="white">-</Typography>}
                <TimeSelect>
                  <Typography>
                    {daySchedule.available === 1 ? convertTimeTo12HourFormat(daySchedule.workdayTo) : ''}
                  </Typography>
                </TimeSelect>
              </TimeCell>
            </TimeRow>
          ))}
        </GridContainer>
      </OperatingHoursGrid>
    </OperatingHoursContainer>
  );
};

export default OperatingHours;
